import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import StatusBadge from "../../components/CustomComponents/StatusBadge/StatusBadge";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import { resolveMediaUrl } from "../../utils/marketingTemplateUtils";
import "./categoryProductsSection.scss";

const CURRENCY_SYMBOLS = {
  GBP: "£",
  USD: "$",
  EUR: "€",
};

const getCurrencySymbol = (code) => CURRENCY_SYMBOLS[code] || code || "£";

const normalizeImageSrc = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return resolveMediaUrl(path);
};

const ProductCard = ({
  item,
  isDarkMode,
  themeColor,
  currencySymbol,
  index,
  canDragReorder,
  onDragStart,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
  onDragEnd,
}) => {
  const [imageError, setImageError] = useState(false);
  const imageSrc = normalizeImageSrc(item.menuItem_Image);
  const salePrice =
    item.menuItem_SalePrice != null ? Number(item.menuItem_SalePrice).toFixed(2) : "—";

  return (
    <article
      className="category-products-section__card"
      style={{ borderColor: isDarkMode ? "#495057" : "#e9ecef" }}
      draggable={canDragReorder}
      onDragStart={(e) => canDragReorder && onDragStart(e, index)}
      onDragEnter={(e) => canDragReorder && onDragEnter(e, index)}
      onDragLeave={onDragLeave}
      onDragOver={(e) => canDragReorder && onDragOver(e)}
      onDrop={(e) => canDragReorder && onDrop(e)}
      onDragEnd={onDragEnd}
    >
      <button
        type="button"
        className="category-products-section__drag-handle"
        aria-label="Drag to reorder product"
        tabIndex={-1}
        style={{
          cursor: canDragReorder ? "grab" : "not-allowed",
          opacity: canDragReorder ? 1 : 0.35,
        }}
      >
        <i className="fas fa-grip-vertical" aria-hidden="true" />
      </button>

      <div className="category-products-section__image-wrap">
        {imageSrc && !imageError ? (
          <img
            src={imageSrc}
            alt={item.menuItem_Name || "Product"}
            className="category-products-section__image"
            onError={() => setImageError(true)}
          />
        ) : (
          <i className="fa fa-utensils category-products-section__image-placeholder" aria-hidden="true" />
        )}
      </div>

      <div className="category-products-section__body">
        <h6
          className="category-products-section__name"
          style={{ color: isDarkMode ? "#f8f9fa" : "#212529" }}
        >
          {item.menuItem_Name}
        </h6>

        <div className="category-products-section__meta">
          <p className="category-products-section__price" style={{ color: themeColor }}>
            {currencySymbol}
            {salePrice}
          </p>
          <StatusBadge isActive={item.menuItem_Publish} disabled />
        </div>

        <Link
          to={`/items/updateItem/${item.menuItem_ID}`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-sm btn-outline-secondary rounded-pill mt-1 align-self-start"
          style={{
            fontSize: "12px",
            borderColor: isDarkMode ? "#6c757d" : undefined,
            color: isDarkMode ? "#e9ecef" : undefined,
          }}
          draggable={false}
        >
          <i className="fa fa-pencil me-1" aria-hidden="true" />
          Edit product
        </Link>
      </div>
    </article>
  );
};

const CategoryProductsSection = ({
  compID,
  rootCatID,
  isDarkMode,
  themeColor,
  onCountChange,
}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [compCurrency, setCompCurrency] = useState("GBP");
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  useEffect(() => {
    if (!compID) return;
    axiosInstance
      .get(APIs.GET_COMPANY_PROFILE)
      .then((response) => {
        setCompCurrency(response.data.data?.compCurrency || "GBP");
      })
      .catch(() => {});
  }, [compID]);

  const fetchProducts = useCallback(async () => {
    if (!compID || !rootCatID) return;

    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_ITEMS_FOR_GRID, {
        params: {
          CompID: compID,
          SortBy: "CategoryItem_SortOrder",
          SortOrder: "ASC",
          Search: "",
          PageNumber: 1,
          PageSize: 500,
          RootCat_ID: rootCatID,
          Status: "",
        },
      });

      const itemsData = response.data.data || response.data;
      const list = Array.isArray(itemsData) ? itemsData : [];
      const total = response.data.totalRecords ?? list.length;
      setProducts(list);
      if (typeof onCountChange === "function") {
        onCountChange(total);
      }
    } catch {
      setProducts([]);
      if (typeof onCountChange === "function") {
        onCountChange(0);
      }
    } finally {
      setLoading(false);
    }
  }, [compID, rootCatID, onCountChange]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const resetDragState = () => {
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragStart = (e, index) => {
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify(index));
    e.currentTarget.classList.add("category-products-section__card--dragging");
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    e.currentTarget.classList.add("category-products-section__card--drag-over");
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove("category-products-section__card--drag-over");
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    document
      .querySelectorAll(".category-products-section__card")
      .forEach((card) => {
        card.classList.remove("category-products-section__card--dragging");
        card.classList.remove("category-products-section__card--drag-over");
      });

    const draggedIndex = JSON.parse(e.dataTransfer.getData("application/json"));
    const overIndex = dragOverItem.current;

    if (
      draggedIndex === overIndex ||
      draggedIndex === undefined ||
      overIndex === undefined
    ) {
      resetDragState();
      return;
    }

    const newProducts = [...products];
    const draggedProduct = newProducts[draggedIndex];
    newProducts.splice(draggedIndex, 1);
    newProducts.splice(overIndex, 0, draggedProduct);

    const updatedProducts = newProducts.map((item, index) => ({
      ...item,
      categoryItem_SortOrder: index + 1,
    }));

    if (updatedProducts.some((item) => !item.relation_ID)) {
      showErrorToast("Cannot reorder items without a category relation.");
      resetDragState();
      fetchProducts();
      return;
    }

    setProducts(updatedProducts);

    try {
      await axiosInstance.post(APIs.POST_UPDATE_ROOT_CATEGORY_ITEM_SORT_ORDER, {
        CompID: compID,
        RootCat_ID: rootCatID,
        Updates: updatedProducts.map((item) => ({
          ID: item.relation_ID,
          SortOrder: item.categoryItem_SortOrder,
        })),
      });
      showSuccessToast("Product order updated for this category.");
      fetchProducts();
    } catch {
      showErrorToast("Failed to update product order.");
      fetchProducts();
    }

    resetDragState();
  };

  const handleDragEnd = (e) => {
    e.currentTarget.classList.remove("category-products-section__card--dragging");
    document
      .querySelectorAll(".category-products-section__card")
      .forEach((card) => card.classList.remove("category-products-section__card--drag-over"));
    resetDragState();
  };

  const currencySymbol = getCurrencySymbol(compCurrency);
  const rootClass = `category-products-section${isDarkMode ? " category-products-section--dark" : ""}`;
  const canDragReorder = products.length > 1;

  return (
    <div className={rootClass}>
      {canDragReorder && !loading && (
        <p className="category-products-section__hint" style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
          <i className="fas fa-grip-vertical me-2" aria-hidden="true" />
          Drag cards to set the display order for this category.
        </p>
      )}

      <GridSectionLoader loading={loading} minHeight="220px">
        {!loading && products.length === 0 ? (
          <div className="category-products-section__empty">
            <i
              className="fa fa-box-open mb-3"
              style={{ fontSize: "32px", color: isDarkMode ? "#6c757d" : "#adb5bd" }}
              aria-hidden="true"
            />
            <p className="mb-0" style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
              No products are assigned to this category yet.
            </p>
          </div>
        ) : (
          <div className="category-products-section__grid">
            {products.map((item, index) => (
              <ProductCard
                key={item.menuItem_ID}
                item={item}
                index={index}
                isDarkMode={isDarkMode}
                themeColor={themeColor}
                currencySymbol={currencySymbol}
                canDragReorder={canDragReorder}
                onDragStart={handleDragStart}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragEnd={handleDragEnd}
              />
            ))}
          </div>
        )}
      </GridSectionLoader>
    </div>
  );
};

export default CategoryProductsSection;
